import { create } from 'zustand';
import { http } from '@/lib/ats-https';
import { getErrorMessage } from '@/utils/errors';
import { handleFraudDetectorError } from '@/utils/errors/Modules/fraud-detector';
import type { VerificationData, OverrideStatusPayload } from './types';

interface FraudDetectionStore {
  data: VerificationData | null;
  isLoading: boolean;
  error: string | null;
  isOverriding: boolean;
  isRetrying: boolean;
  fetchVerificationDetails: (_applicationId: number) => Promise<void>;
  overrideStatus: (
    _applicationId: number,
    _payload: OverrideStatusPayload
  ) => Promise<boolean>;
  retryVerification: (_applicationId: number) => Promise<string | null>;
  reset: () => void;
}

export const useFraudDetectionStore = create<FraudDetectionStore>((set) => ({
  data: null,
  isLoading: false,
  error: null,
  isOverriding: false,
  isRetrying: false,

  fetchVerificationDetails: async (applicationId: number) => {
    set({ isLoading: true, error: null });
    try {
      const response = await http.get(
        `/contact_background_verification/${applicationId}/details`
      );
      set({ data: response as unknown as VerificationData, isLoading: false });
    } catch (error) {
      set({
        error: getErrorMessage(
          error,
          'fraudDetector',
          handleFraudDetectorError
        ),
        isLoading: false,
      });
    }
  },

  overrideStatus: async (
    applicationId: number,
    payload: OverrideStatusPayload
  ) => {
    set({ isOverriding: true });
    try {
      await http.put(
        `/ats_applications/${applicationId}/contact_background_verification/override_status`,
        payload
      );
      set({ isOverriding: false });
      return true;
    } catch {
      set({ isOverriding: false });
      return false;
    }
  },

  retryVerification: async (applicationId: number) => {
    set({ isRetrying: true });
    try {
      const response = await http.post(
        `/ats_applications/${applicationId}/contact_background_verification/verifications?retry_with_new_details=true`,
        {}
      );
      const responseData = response as any;
      const verificationErrorCode =
        responseData?.verification?.error_code ?? null;
      const errorCode =
        verificationErrorCode ??
        responseData?.error_codes?.[0] ??
        responseData?.error_code ??
        null;

      set({ isRetrying: false });
      if (errorCode) {
        return errorCode;
      }
      if (responseData?.verification) {
        return null;
      }
      return errorCode;
    } catch {
      set({ isRetrying: false });
      return 'unknown_error';
    }
  },

  reset: () =>
    set({
      data: null,
      isLoading: false,
      error: null,
      isOverriding: false,
      isRetrying: false,
    }),
}));
